import type { ResolvedVisualThemeId } from './visualThemeTypes';
import { SOUND_ASSET_URLS } from './themeSoundAssets';

// ---------------------------------------------------------------------------
// Helpers: map a resolved theme id to the correct sound asset.
// All functions accept ResolvedVisualThemeId (never 'auto') so callers must
// use getActiveResolvedVisualThemeId() rather than getActiveVisualThemeId().
//
// Grouping:
//   varangian           → norse/axe/horn assets
//   dark | cosmos       → dark/muted assets  (cosmos is the night auto-theme)
//   neutral | sun       → neutral assets     (sun is the day auto-theme)
// ---------------------------------------------------------------------------

function isDarkLike(id: ResolvedVisualThemeId): boolean {
  return id === 'dark' || id === 'cosmos';
}

function isNorseLike(id: ResolvedVisualThemeId): boolean {
  return id === 'varangian';
}

// --- Incoming message sound -------------------------------------------------

/** Android raw basename (no extension). */
export function messageSoundAndroidRes(id: ResolvedVisualThemeId): string {
  if (isNorseLike(id)) return 'message_sword';
  if (isDarkLike(id)) return 'message_dark';
  return 'message_neutral';
}

export function messageSoundIosFilename(id: ResolvedVisualThemeId): string {
  if (isNorseLike(id)) return 'message_sword.wav';
  if (isDarkLike(id)) return 'message_dark.wav';
  return 'message_neutral.wav';
}

export function messageSoundWebAsset(id: ResolvedVisualThemeId): string {
  return SOUND_ASSET_URLS[messageSoundIosFilename(id)] ?? '';
}

// --- Incoming call ringtone -------------------------------------------------

export function ringSoundAndroidRes(id: ResolvedVisualThemeId): string {
  if (isNorseLike(id)) return 'ring_horn';
  if (isDarkLike(id)) return 'ring_dark';
  return 'ring_neutral';
}

export function ringSoundIosFilename(id: ResolvedVisualThemeId): string {
  if (isNorseLike(id)) return 'ring_horn.wav';
  if (isDarkLike(id)) return 'ring_dark.wav';
  return 'ring_neutral.wav';
}

export function ringSoundWebAsset(id: ResolvedVisualThemeId): string {
  return SOUND_ASSET_URLS[ringSoundIosFilename(id)] ?? '';
}

// --- Outgoing call ringback -------------------------------------------------

/** Android raw resource name (no extension) for the outgoing call ringback. */
export function ringbackSoundAndroidRes(id: ResolvedVisualThemeId): string {
  if (isNorseLike(id)) return 'outgoing_ringback';
  if (isDarkLike(id)) return 'outgoing_ringback_dark';
  return 'outgoing_ringback_neutral';
}

/** iOS Main Bundle filename for the outgoing call ringback. */
export function ringbackSoundIosFilename(id: ResolvedVisualThemeId): string {
  if (isNorseLike(id)) return 'outgoing_ringback.mp3';
  if (isDarkLike(id)) return 'outgoing_ringback_dark.wav';
  return 'outgoing_ringback_neutral.wav';
}

export function ringbackSoundWebAsset(id: ResolvedVisualThemeId): string {
  return SOUND_ASSET_URLS[ringbackSoundIosFilename(id)] ?? '';
}

// --- Short call-UI tones (answer / hangup beeps) ---------------------------
//
// Общие для всех тем и намеренно: это не голос темы, а служебная отбивка «соединились» /
// «разговор окончен», её задача — быть одинаковой и незаметной.
//
// Раньше здесь возвращался файл гудка, который `playCallUiTone` обрывал через 170/120 мс.
// Обрыв без фейда — щелчок, а в варяжской теме это были первые полтакта «Полёта валькирий»,
// оборванные посреди ноты. Теперь два своих коротких тона: восходящий на ответ, нисходящий
// на завершение.

export type CallUiTone = 'answer' | 'end';

export function callUiToneAndroidRes(tone: CallUiTone): string {
  return tone === 'answer' ? 'tone_answer' : 'tone_end';
}

export function callUiToneIosFilename(tone: CallUiTone): string {
  return tone === 'answer' ? 'tone_answer.wav' : 'tone_end.wav';
}

export function callUiToneWebAsset(tone: CallUiTone): string {
  return SOUND_ASSET_URLS[callUiToneIosFilename(tone)] ?? '';
}
